'use client';

/**
 * Presentational sections for the library catalog page.
 *
 * Module: app/library/book-grid
 * Layer:  component
 *
 * Purpose:
 *     Renders the search + filter toolbar, the responsive book grid, the
 *     empty / error states, and the authenticated "My checkouts" and "Reading
 *     assignments" panels. Each piece is a small component so every function
 *     clears the CC<=5 / <=60-line gates and the page file stays thin.
 *
 * AI context:
 *     - Entry points called by: src/app/library/page.tsx (LibraryContent).
 *     - Depends on: components/library/BookCard, lib/library (token tables),
 *       lib/i18n, the LibraryController from use-library.
 *     - Side effects: none; user input is delegated to controller callbacks.
 *     - Invariants: purely presentational; reads the controller, never fetches.
 *     - Replacement cost: LOW.
 */

import { type ChangeEvent, type SubmitEvent, useCallback } from 'react';
import { BookCard } from '@/components/library/BookCard';
import { type TranslationKey, useTranslation } from '@/lib/i18n';
import {
  type BookCheckout,
  type BookType,
  type ReadingAssignment,
} from '@/lib/library';
import type { LibraryController } from './use-library';

/** Book-type filter options in display order (data-driven). */
const TYPE_OPTIONS: readonly { readonly value: BookType | 'all'; readonly labelKey: TranslationKey }[] =
  [
    { value: 'all', labelKey: 'library.filter.allTypes' },
    { value: 'ebook', labelKey: 'library.type.ebook' },
    { value: 'audiobook', labelKey: 'library.type.audiobook' },
    { value: 'physical', labelKey: 'library.type.physical' },
    { value: 'interactive', labelKey: 'library.type.interactive' },
    { value: 'video', labelKey: 'library.type.video' },
  ];

/** Checkout status -> i18n label key (data-driven). */
const CHECKOUT_STATUS_KEY: Readonly<Record<BookCheckout['status'], TranslationKey>> = {
  checked_out: 'library.checkouts.status.checked_out',
  returned: 'library.checkouts.status.returned',
  overdue: 'library.checkouts.status.overdue',
  lost: 'library.checkouts.status.lost',
};

/** Format an ISO date string as a locale date, or the raw value on failure. */
function formatDate(iso: string): string {
  const parsed = new Date(iso);
  return Number.isNaN(parsed.getTime()) ? iso : parsed.toLocaleDateString();
}

/** Search box + book-type + category selectors. */
export function Toolbar(props: { readonly ctrl: LibraryController }): React.JSX.Element {
  const { t } = useTranslation();
  const { ctrl } = props;
  const onSubmit = useCallback(
    (e: SubmitEvent<HTMLFormElement>): void => {
      e.preventDefault();
      ctrl.submitSearch();
    },
    [ctrl],
  );
  const onSearchChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>): void => ctrl.setDraftSearch(e.target.value),
    [ctrl],
  );
  const onTypeChange = useCallback(
    (e: ChangeEvent<HTMLSelectElement>): void => ctrl.setBookType(e.target.value as BookType | 'all'),
    [ctrl],
  );
  const onCategoryChange = useCallback(
    (e: ChangeEvent<HTMLSelectElement>): void => ctrl.setCategory(e.target.value),
    [ctrl],
  );
  return (
    <div className='mb-8 flex flex-col gap-4 md:flex-row'>
      <form onSubmit={onSubmit} className='flex-1'>
        <input
          type='search'
          value={ctrl.draftSearch}
          onChange={onSearchChange}
          placeholder={t('library.searchPlaceholder')}
          className='w-full rounded-xl border px-4 py-3 focus:border-blue-500 focus:ring-2 focus:ring-blue-500'
        />
      </form>
      <select
        value={ctrl.filters.bookType}
        onChange={onTypeChange}
        aria-label={t('library.filter.typeLabel')}
        className='rounded-xl border bg-white px-4 py-3 focus:ring-2 focus:ring-blue-500'
      >
        {TYPE_OPTIONS.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {t(opt.labelKey)}
          </option>
        ))}
      </select>
      <CategorySelect ctrl={ctrl} onChange={onCategoryChange} />
    </div>
  );
}

/** Category dropdown populated from the loaded catalog facets. */
function CategorySelect(props: {
  readonly ctrl: LibraryController;
  readonly onChange: (e: ChangeEvent<HTMLSelectElement>) => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const { ctrl, onChange } = props;
  return (
    <select
      value={ctrl.filters.category}
      onChange={onChange}
      aria-label={t('library.filter.categoryLabel')}
      className='rounded-xl border bg-white px-4 py-3 focus:ring-2 focus:ring-blue-500'
    >
      <option value='all'>{t('library.filter.allCategories')}</option>
      {ctrl.categories.map((cat) => (
        <option key={cat} value={cat}>
          {cat}
        </option>
      ))}
    </select>
  );
}

/** Responsive catalog grid (2/3/4 columns) or an empty-state panel. */
export function BookGrid(props: { readonly ctrl: LibraryController }): React.JSX.Element {
  const { t } = useTranslation();
  const { ctrl } = props;
  if (ctrl.books.length === 0) {
    return (
      <div className='rounded-xl bg-gray-100 py-12 text-center'>
        <h3 className='mb-2 text-lg font-semibold'>{t('library.empty.title')}</h3>
        <p className='mb-4 opacity-70'>{t('library.empty.subtitle')}</p>
        <button
          type='button'
          onClick={ctrl.clearFilters}
          className='rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700'
        >
          {t('library.empty.clear')}
        </button>
      </div>
    );
  }
  return (
    <div className='grid grid-cols-2 gap-6 md:grid-cols-3 lg:grid-cols-4'>
      {ctrl.books.map((book) => (
        <BookCard
          key={book.id}
          book={book}
          canCheckout={ctrl.authenticated}
          isCheckingOut={ctrl.busyBookId === book.id}
          onCheckout={ctrl.checkout}
        />
      ))}
    </div>
  );
}

/** A single checkout row with a return button. */
function CheckoutRow(props: {
  readonly checkout: BookCheckout;
  readonly busy: boolean;
  readonly onReturn: (id: number) => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const { checkout, busy, onReturn } = props;
  const handleReturn = useCallback((): void => onReturn(checkout.id), [onReturn, checkout.id]);
  return (
    <li className='flex items-center justify-between rounded-lg border bg-white p-4'>
      <div className='min-w-0'>
        <p className='truncate font-medium'>{checkout.book_title}</p>
        <p className='text-sm opacity-70'>
          {t('library.checkouts.dueDate', { date: formatDate(checkout.due_date) })}
          {' · '}
          {t(CHECKOUT_STATUS_KEY[checkout.status])}
        </p>
      </div>
      <button
        type='button'
        onClick={handleReturn}
        disabled={busy}
        className='ml-4 shrink-0 rounded-lg border border-blue-600 px-3 py-2 text-sm font-medium text-blue-600 hover:bg-blue-50 disabled:opacity-60'
      >
        {busy ? t('library.checkouts.returning') : t('library.checkouts.return')}
      </button>
    </li>
  );
}

/** "My checkouts" panel; renders nothing when the student has none. */
export function CheckoutsSection(props: {
  readonly ctrl: LibraryController;
}): React.JSX.Element | null {
  const { t } = useTranslation();
  const { ctrl } = props;
  if (!ctrl.authenticated || ctrl.checkouts.length === 0) {
    return null;
  }
  return (
    <section className='mb-12'>
      <h2 className='mb-4 text-2xl font-bold'>{t('library.checkouts.title')}</h2>
      <ul className='space-y-3'>
        {ctrl.checkouts.map((checkout) => (
          <CheckoutRow
            key={checkout.id}
            checkout={checkout}
            busy={ctrl.busyCheckoutId === checkout.id}
            onReturn={ctrl.returnCheckout}
          />
        ))}
      </ul>
    </section>
  );
}

/** A single reading-assignment row with a progress label. */
function AssignmentRow(props: { readonly assignment: ReadingAssignment }): React.JSX.Element {
  const { t } = useTranslation();
  const { assignment } = props;
  const due = assignment.due_date
    ? t('library.assignments.dueDate', { date: formatDate(assignment.due_date) })
    : t('library.assignments.noDueDate');
  return (
    <li className='rounded-lg border bg-white p-4'>
      <p className='truncate font-medium'>{assignment.book_title}</p>
      <p className='text-sm opacity-70'>
        {due}
        {' · '}
        {t('library.assignments.progress', { percent: Math.round(assignment.progress_percent) })}
      </p>
    </li>
  );
}

/** "Reading assignments" panel; renders nothing when there are none. */
export function AssignmentsSection(props: {
  readonly ctrl: LibraryController;
}): React.JSX.Element | null {
  const { t } = useTranslation();
  const { ctrl } = props;
  if (!ctrl.authenticated || ctrl.assignments.length === 0) {
    return null;
  }
  return (
    <section className='mb-12'>
      <h2 className='mb-4 text-2xl font-bold'>{t('library.assignments.title')}</h2>
      <ul className='space-y-3'>
        {ctrl.assignments.map((assignment) => (
          <AssignmentRow key={assignment.id} assignment={assignment} />
        ))}
      </ul>
    </section>
  );
}
